import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Bell,
  AlertTriangle,
  CheckCircle,
  Shield,
  Server,
  User,
  Filter,
  SortAsc,
  SortDesc,
  X,
  Check,
  Trash2,
  Eye
} from 'lucide-react';

interface NotificationsPageProps {
  onBack: () => void;
}

type NotificationType = 'threat' | 'remediation' | 'system' | 'user';

interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  description: string;
  timestamp: Date;
  read: boolean;
  severity?: 'critical' | 'high' | 'medium' | 'low';
  source?: string;
  relatedIncidentId?: string;
}

const NotificationsPage: React.FC<NotificationsPageProps> = ({ onBack }) => {
  // Realistic dummy data
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: 'notif-001',
      type: 'threat',
      title: 'Critical Threat Detected',
      description: 'Crypto Mining Malware detected on EC2 instance i-082ecf59714651549',
      timestamp: new Date(Date.now() - 5 * 60000),
      read: false,
      severity: 'critical',
      source: 'AWS GuardDuty',
      relatedIncidentId: 'demo-aws-001'
    },
    {
      id: 'notif-002',
      type: 'remediation',
      title: 'Auto-Remediation Executed',
      description: 'EC2 instance i-082ecf59714651549 isolated and stopped successfully',
      timestamp: new Date(Date.now() - 4 * 60000),
      read: false,
      source: 'Arbitium AI',
      relatedIncidentId: 'demo-aws-001'
    },
    {
      id: 'notif-003',
      type: 'threat',
      title: 'Credential Theft Attempt',
      description: 'Mimikatz-style LSASS memory access detected on DC-PRIMARY',
      timestamp: new Date(Date.now() - 12 * 60000),
      read: false,
      severity: 'critical',
      source: 'CrowdStrike',
      relatedIncidentId: 'demo-cs-002'
    },
    {
      id: 'notif-004',
      type: 'remediation',
      title: 'Remediation Completed',
      description: 'Host containment initiated for DC-PRIMARY, credentials reset for 3 service accounts',
      timestamp: new Date(Date.now() - 10 * 60000),
      read: true,
      source: 'Arbitium AI',
      relatedIncidentId: 'demo-cs-002'
    },
    {
      id: 'notif-005',
      type: 'system',
      title: 'Pipeline Health Check',
      description: 'All 5 AI pipeline stages operational. Average response time: 1.2s',
      timestamp: new Date(Date.now() - 30 * 60000),
      read: true,
      source: 'System'
    },
    {
      id: 'notif-006',
      type: 'threat',
      title: 'Brute Force Attack Detected',
      description: '47 failed login attempts for user john.smith@company.com from IP 203.0.113.42',
      timestamp: new Date(Date.now() - 45 * 60000),
      read: true,
      severity: 'high',
      source: 'Okta',
      relatedIncidentId: 'demo-okta-001'
    },
    {
      id: 'notif-007',
      type: 'remediation',
      title: 'User Session Terminated',
      description: 'All active sessions for john.smith@company.com revoked, account locked pending review',
      timestamp: new Date(Date.now() - 44 * 60000),
      read: true,
      source: 'Arbitium AI',
      relatedIncidentId: 'demo-okta-001'
    },
    {
      id: 'notif-008',
      type: 'user',
      title: 'Manual Review Requested',
      description: 'Analyst approval required for S3 bucket remediation action',
      timestamp: new Date(Date.now() - 60 * 60000),
      read: true,
      source: 'Workflow'
    },
    {
      id: 'notif-009',
      type: 'system',
      title: 'Integration Connected',
      description: 'CrowdStrike Falcon sensor successfully integrated with Arbitium platform',
      timestamp: new Date(Date.now() - 2 * 60 * 60000),
      read: true,
      source: 'System'
    },
    {
      id: 'notif-010',
      type: 'threat',
      title: 'S3 Bucket Exposure',
      description: 'Public access enabled on bucket arbitium-demo-sensitive-254886179156',
      timestamp: new Date(Date.now() - 3 * 60 * 60000),
      read: true,
      severity: 'high',
      source: 'AWS GuardDuty',
      relatedIncidentId: 'demo-aws-003'
    },
    {
      id: 'notif-011',
      type: 'user',
      title: 'Settings Updated',
      description: 'Auto-remediation mode enabled by admin@arbitium.com',
      timestamp: new Date(Date.now() - 4 * 60 * 60000),
      read: true,
      source: 'User Action'
    },
    {
      id: 'notif-012',
      type: 'system',
      title: 'Daily Threat Report Generated',
      description: '12 threats detected, 11 auto-remediated, 1 pending review',
      timestamp: new Date(Date.now() - 24 * 60 * 60000),
      read: true,
      source: 'Reporting'
    },
  ]);

  const [filterType, setFilterType] = useState<NotificationType | 'all'>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const getTypeIcon = (type: NotificationType) => {
    switch (type) {
      case 'threat': return <AlertTriangle className="w-5 h-5" />;
      case 'remediation': return <Shield className="w-5 h-5" />;
      case 'system': return <Server className="w-5 h-5" />;
      case 'user': return <User className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type: NotificationType) => {
    switch (type) {
      case 'threat': return 'text-red-400 bg-red-400/10';
      case 'remediation': return 'text-green-400 bg-green-400/10';
      case 'system': return 'text-blue-400 bg-blue-400/10';
      case 'user': return 'text-purple-400 bg-purple-400/10';
    }
  };

  const getSeverityBadge = (severity?: string) => {
    if (!severity) return null;
    const colors: Record<string, string> = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/50',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      low: 'bg-green-500/20 text-green-400 border-green-500/50',
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded border ${colors[severity]}`}>
        {severity.toUpperCase()}
      </span>
    );
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return 'Yesterday';
    return date.toLocaleDateString();
  };

  const formatFullTime = (date: Date) => {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const markAsRead = (id: string) => {
    setNotifications(prev => prev.map(n => 
      n.id === id ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const deleteNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearAllRead = () => {
    setNotifications(prev => prev.filter(n => !n.read));
  };

  // Filter and sort notifications
  const filteredNotifications = notifications
    .filter(n => filterType === 'all' || n.type === filterType)
    .filter(n => !showUnreadOnly || !n.read)
    .sort((a, b) => {
      const diff = b.timestamp.getTime() - a.timestamp.getTime();
      return sortOrder === 'newest' ? diff : -diff;
    });

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-arbitium-darker font-nunito">
      {/* Background effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-arbitium-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-arbitium-accent/5 rounded-full blur-3xl"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 bg-arbitium-dark/80 backdrop-blur-xl border-b border-arbitium-primary/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 text-gray-400 hover:text-white hover:bg-arbitium-primary/10 rounded-lg transition-colors"
              title="Back to Dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <Bell className="w-6 h-6 text-arbitium-accent" />
              <h1 className="text-xl font-semibold text-white">Notifications</h1>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium bg-red-500 text-white rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={markAllAsRead}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-arbitium-primary/10 rounded-lg transition-colors"
            >
              <Check className="w-4 h-4" />
              Mark all read
            </button>
            <button
              onClick={clearAllRead}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Clear read
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 p-6">
        <div className="max-w-5xl mx-auto">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-400">Filter:</span>
            </div>
            <div className="flex gap-2">
              {(['all', 'threat', 'remediation', 'system', 'user'] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    filterType === type
                      ? 'bg-arbitium-accent/20 text-arbitium-accent border border-arbitium-accent/50'
                      : 'text-gray-400 hover:text-white hover:bg-arbitium-primary/10 border border-transparent'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
            
            <div className="h-6 border-l border-gray-700 mx-2"></div>
            
            <button
              onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 hover:text-white hover:bg-arbitium-primary/10 rounded-lg transition-colors"
            >
              {sortOrder === 'newest' ? <SortDesc className="w-4 h-4" /> : <SortAsc className="w-4 h-4" />}
              {sortOrder === 'newest' ? 'Newest first' : 'Oldest first'}
            </button>
            
            <button
              onClick={() => setShowUnreadOnly(!showUnreadOnly)}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                showUnreadOnly
                  ? 'bg-arbitium-accent/20 text-arbitium-accent border border-arbitium-accent/50'
                  : 'text-gray-400 hover:text-white hover:bg-arbitium-primary/10 border border-transparent'
              }`}
            >
              <Eye className="w-4 h-4" />
              Unread only
            </button>
          </div>

          {/* Notifications List */}
          <div className="bg-arbitium-dark/50 backdrop-blur-xl border border-arbitium-primary/10 rounded-xl overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-arbitium-primary/10 text-sm text-gray-500 font-medium">
              <div className="col-span-1">Type</div>
              <div className="col-span-5">Details</div>
              <div className="col-span-2">Source</div>
              <div className="col-span-2">Time</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>

            {/* Notifications */}
            {filteredNotifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No notifications found
              </div>
            ) : (
              <div className="divide-y divide-arbitium-primary/10">
                {filteredNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`grid grid-cols-12 gap-4 px-4 py-4 hover:bg-arbitium-primary/5 transition-colors ${
                      !notification.read ? 'bg-arbitium-accent/5' : ''
                    }`}
                  >
                    {/* Type Icon */}
                    <div className="col-span-1 flex items-start">
                      <div className={`p-2 rounded-lg ${getTypeColor(notification.type)}`}>
                        {getTypeIcon(notification.type)}
                      </div>
                    </div>

                    {/* Details */}
                    <div className="col-span-5">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className={`font-medium ${!notification.read ? 'text-white' : 'text-gray-300'}`}>
                          {notification.title}
                        </h3>
                        {!notification.read && (
                          <span className="w-2 h-2 bg-arbitium-accent rounded-full"></span>
                        )}
                        {getSeverityBadge(notification.severity)}
                      </div>
                      <p className="text-sm text-gray-500">{notification.description}</p>
                    </div>

                    {/* Source */}
                    <div className="col-span-2 flex items-center">
                      <span className="text-sm text-gray-400">{notification.source}</span>
                    </div>

                    {/* Time */}
                    <div className="col-span-2 flex items-center">
                      <div>
                        <p className="text-sm text-gray-400">{formatTime(notification.timestamp)}</p>
                        <p className="text-xs text-gray-600">{formatFullTime(notification.timestamp)}</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="col-span-2 flex items-center justify-end gap-2">
                      {!notification.read && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="p-2 text-gray-400 hover:text-green-400 hover:bg-green-500/10 rounded-lg transition-colors"
                          title="Mark as read"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notification.id)}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="mt-4 text-sm text-gray-500 text-center">
            Showing {filteredNotifications.length} of {notifications.length} notifications
          </div>
        </div>
      </main>
    </div>
  );
};

export default NotificationsPage;
